import React, { useState, useRef } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Avatar,
  Grid,
  TextField,
  Button,
  Chip,
  Card,
  CardContent,
  IconButton,
  Divider,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  PhotoCamera as PhotoCameraIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Work as WorkIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
  Update as UpdateIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

interface UserProfile {
  id: string;
  fullName: string;
  email: string;
  position: string;
  branch: string;
  profileImage?: string;
  createdAt: string;
  updatedAt: string;
  status: 'active' | 'inactive';
}

const ProfilePage: React.FC = () => {
  const { currentUser } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // حالة الملف الشخصي
  const [profile, setProfile] = useState<UserProfile>({
    id: currentUser?.id || '1',
    fullName: 'أحمد السواح',
    email: 'baballouk@gmail.com',
    position: 'مدير النظام',
    branch: 'الصيانة باب اللوق',
    profileImage: '',
    createdAt: '2023-01-15T10:30:00.000Z', // تاريخ إنشاء الحساب بالميلادي
    updatedAt: new Date().toISOString(),
    status: 'active'
  });

  // حالة التحرير
  const [isEditing, setIsEditing] = useState(false);
  const [editedProfile, setEditedProfile] = useState<UserProfile>(profile);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning' | 'info'
  });

  // معالجة تحديث الصورة
  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const imageUrl = e.target?.result as string;
        if (isEditing) {
          setEditedProfile(prev => ({ ...prev, profileImage: imageUrl }));
        } else {
          setProfile(prev => ({ ...prev, profileImage: imageUrl }));
          // حفظ الصورة في localStorage
          localStorage.setItem('userProfileImage', imageUrl);
          setSnackbar({
            open: true,
            message: 'تم تحديث صورة الملف الشخصي بنجاح',
            severity: 'success'
          });
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // بدء التحرير
  const handleEdit = () => {
    setIsEditing(true);
    setEditedProfile(profile);
  };

  // إلغاء التحرير
  const handleCancel = () => {
    setIsEditing(false);
    setEditedProfile(profile);
  };

  // حفظ التغييرات
  const handleSave = () => {
    try {
      const updatedProfile = {
        ...editedProfile,
        updatedAt: new Date().toISOString()
      };
      
      setProfile(updatedProfile);
      
      // حفظ البيانات في localStorage
      localStorage.setItem('userProfile', JSON.stringify(updatedProfile));
      
      setIsEditing(false);
      setSnackbar({
        open: true,
        message: 'تم حفظ التغييرات بنجاح',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'فشل في حفظ التغييرات',
        severity: 'error'
      });
    }
  };

  // تحديث قيم النموذج
  const handleInputChange = (field: keyof UserProfile, value: string) => {
    setEditedProfile(prev => ({ ...prev, [field]: value }));
  };

  // تنسيق التاريخ بالميلادي
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('ar-EG', {
        calendar: 'gregory', // التقويم الميلادي
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return 'غير محدد';
    }
  };

  // تحميل البيانات المحفوظة عند بدء التشغيل
  React.useEffect(() => {
    const savedProfile = localStorage.getItem('userProfile');
    const savedImage = localStorage.getItem('userProfileImage');
    
    if (savedProfile) {
      try {
        const parsedProfile = JSON.parse(savedProfile);
        setProfile(parsedProfile);
      } catch (error) {
        console.error('Error loading saved profile:', error);
      }
    }
    
    if (savedImage) {
      setProfile(prev => ({ ...prev, profileImage: savedImage }));
    }
  }, []);

  const currentProfile = isEditing ? editedProfile : profile;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* رأس الصفحة */}
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={3}>
            {/* صورة الملف الشخصي */}
            <Box position="relative">
              <Avatar
                src={currentProfile.profileImage}
                sx={{ 
                  width: 120, 
                  height: 120,
                  border: '4px solid',
                  borderColor: 'primary.main'
                }}
              >
                <PersonIcon sx={{ fontSize: 60 }} />
              </Avatar>
              <IconButton
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  right: 0,
                  bgcolor: 'primary.main',
                  color: 'white',
                  '&:hover': { bgcolor: 'primary.dark' }
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                <PhotoCameraIcon />
              </IconButton>
              <input
                type="file"
                ref={fileInputRef}
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleImageUpload}
              />
            </Box>

            {/* معلومات أساسية */}
            <Box>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                {currentProfile.fullName}
              </Typography>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {currentProfile.position}
              </Typography>
              <Chip
                label={currentProfile.status === 'active' ? 'نشط' : 'غير نشط'}
                color={currentProfile.status === 'active' ? 'success' : 'error'}
                icon={<CheckCircleIcon />}
              />
            </Box>
          </Box>

          {/* أزرار التحكم */}
          <Box>
            {!isEditing ? (
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={handleEdit}
              >
                تعديل الملف الشخصي
              </Button>
            ) : (
              <Box display="flex" gap={1}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                >
                  حفظ
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={handleCancel}
                >
                  إلغاء
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </Paper>

      <Grid container spacing={3}>
        {/* المعلومات الشخصية */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                <PersonIcon color="primary" />
                المعلومات الشخصية
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box display="flex" flexDirection="column" gap={2}>
                <TextField
                  label="الاسم الكامل"
                  value={currentProfile.fullName}
                  onChange={(e) => handleInputChange('fullName', e.target.value)}
                  disabled={!isEditing}
                  fullWidth
                  InputProps={{
                    startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  }}
                />
                
                <TextField
                  label="البريد الإلكتروني"
                  value={currentProfile.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  disabled={!isEditing}
                  fullWidth
                  InputProps={{
                    startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  }}
                />
                
                <TextField
                  label="المنصب"
                  value={currentProfile.position}
                  onChange={(e) => handleInputChange('position', e.target.value)}
                  disabled={!isEditing}
                  fullWidth
                  InputProps={{
                    startAdornment: <WorkIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  }}
                />
                
                <TextField
                  label="الفرع"
                  value={currentProfile.branch}
                  onChange={(e) => handleInputChange('branch', e.target.value)}
                  disabled={!isEditing}
                  fullWidth
                  InputProps={{
                    startAdornment: <BusinessIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* معلومات الحساب */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                <CalendarIcon color="primary" />
                معلومات الحساب
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box display="flex" flexDirection="column" gap={2}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    تاريخ الإنشاء
                  </Typography>
                  <Typography variant="body1" display="flex" alignItems="center" gap={1}>
                    <CalendarIcon fontSize="small" color="action" />
                    {formatDate(currentProfile.createdAt)}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    آخر تحديث
                  </Typography>
                  <Typography variant="body1" display="flex" alignItems="center" gap={1}>
                    <UpdateIcon fontSize="small" color="action" />
                    {formatDate(currentProfile.updatedAt)}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    حالة الحساب
                  </Typography>
                  <Chip
                    label={currentProfile.status === 'active' ? 'نشط' : 'غير نشط'}
                    color={currentProfile.status === 'active' ? 'success' : 'error'}
                    icon={<CheckCircleIcon />}
                    size="small"
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* رسائل التأكيد */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ProfilePage;
